'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getInviteToBuyGUID } from '@src/actions/social-ladder.actions';
import { configPublic } from '@src/config.public';
import { useInterval } from '@src/hooks/useInterval';
import { usePageVisibility } from '@src/hooks/usePageVisibility';
import { useMarketingAttribution } from '@src/utils/analytics.util';
import { COOKIES, ONE_DAY_IN_SECONDS, QUERY_PARAMS } from '@src/utils/constants.util';
import { getBrowserCookie, setBrowserCookie } from '@src/utils/cookies.util';
import { log } from '@src/utils/log.util';
import { WebSourceCodesSet } from '@src/utils/web-source-code.util';
import { instrumentAction } from "@src/utils/instrumentation";
export const initialState = {
  socialladderAmbassadorId: '' as string | undefined,
  setSocialladderAmbassadorId: (_socialladderAmbassadorId: string) => {},
  royaltyCode: '',
  setRoyaltyCode: (_royaltyCode: string) => {},
  webSourceCode: '',
  setWebSourceCode: (_webSourceCode: string) => {},
  isRecaptchaEnabled: false,
  setIsRecaptchaEnabled: (_isRecaptchaEnabled: boolean) => {}
};
export const TrackingCodesContext = createContext(initialState);
const logNampsace = '[TrackingCodes]';

/**
 * Manages different tracking codes in the app.
 * The codes are ingested from Builder page fields, URL query or cookies.
 * The codes are stored in cookies.
 */
export const TrackingCodesProvider = t0 => {
  const $ = _c(34);
  const {
    children,
    serverData
  } = t0;
  const [royaltyCode, setRoyaltyCode] = useState("");
  const [webSourceCode, setWebSourceCode] = useState("");
  const [isRecaptchaEnabled, setIsRecaptchaEnabled] = useState(true);
  const searchParams = useSearchParams();
  const {
    isPageVisible
  } = usePageVisibility();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get(QUERY_PARAMS.socialladderAmbassadorId);
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const slAmbassadorIdFromQuery = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getBrowserCookie(COOKIES.socialladderAmbassadorId);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const slAmbassadorIdFromCookie = t2;
  const [socialladderAmbassadorId, setSocialladderAmbassadorId] = useState(slAmbassadorIdFromCookie);
  const [loadSocialLadderAttempts, setLoadSocialLadderAttempts] = useState(0);
  const [loadSocialLadderCompleted, setLoadSocialLadderCompleted] = useState(false);
  const shouldLoadSocialladderAmbassadorId = slAmbassadorIdFromQuery && !loadSocialLadderCompleted && loadSocialLadderAttempts < 5;
  useMarketingAttribution(QUERY_PARAMS.nextDoorClickId);
  let t3;
  if ($[3] !== shouldLoadSocialladderAmbassadorId || $[4] !== slAmbassadorIdFromQuery) {
    t3 = () => {
      if (!shouldLoadSocialladderAmbassadorId) {
        return;
      }
      setLoadSocialLadderAttempts(_temp);
      instrumentAction("SocialLadderActions.getInviteToBuyGUID", getInviteToBuyGUID)(slAmbassadorIdFromQuery).then(inviteToBuyGUID => {
        if (!inviteToBuyGUID) {
          return;
        }
        setLoadSocialLadderCompleted(true);
        setSocialladderAmbassadorId(inviteToBuyGUID);
        setBrowserCookie(COOKIES.socialladderAmbassadorId, inviteToBuyGUID, `path=/; max-age=${configPublic.socialLadder.cookieMaxAge}`);
      });
    };
    $[3] = shouldLoadSocialladderAmbassadorId;
    $[4] = slAmbassadorIdFromQuery;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const loadSocialladderAmbassadorId = t3;
  useInterval(loadSocialladderAmbassadorId, shouldLoadSocialladderAmbassadorId ? 5000 : null);
  let t4;
  if ($[6] !== searchParams || $[7] !== serverData?.royaltyCode) {
    t4 = () => {
      const codeFromServer = serverData?.royaltyCode;
      const codeFromQuery = searchParams?.get(QUERY_PARAMS.royaltyCodeAlt) ?? searchParams?.get(COOKIES.royaltyCode);
      const codeFromCookie = getBrowserCookie(COOKIES.royaltyCode);
      const codeResolved = codeFromQuery ?? codeFromServer ?? codeFromCookie;
      if (!codeResolved) {
        return;
      }
      if (codeResolved !== codeFromCookie) {
        const cookieOptions = `path=/; max-age=${ONE_DAY_IN_SECONDS * 30}`;
        setBrowserCookie(COOKIES.royaltyCode, codeResolved, cookieOptions);
      }
      setRoyaltyCode(codeResolved);
    };
    $[6] = searchParams;
    $[7] = serverData?.royaltyCode;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  serverData?.royaltyCode;
  const loadRoyaltyCode = t4;
  let t5;
  if ($[9] !== searchParams) {
    t5 = () => {
      const codeFromQuery_0 = searchParams?.get(QUERY_PARAMS.webSourceCodeAlt) ?? searchParams?.get(COOKIES.webSourceCode);
      const codeFromCookie_0 = getBrowserCookie(COOKIES.webSourceCode);
      const webSourceCodesSet = new WebSourceCodesSet(codeFromCookie_0);
      webSourceCodesSet.add(codeFromQuery_0);
      const codeResolved_0 = webSourceCodesSet.serialize();
      if (!codeResolved_0) {
        return;
      }
      if (codeResolved_0 !== codeFromCookie_0) {
        setBrowserCookie(COOKIES.webSourceCode, codeResolved_0);
      }
      setWebSourceCode(codeResolved_0);
    };
    $[9] = searchParams;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const loadWebSourceCode = t5;
  let t6;
  if ($[11] !== searchParams) {
    t6 = () => {
      const codeFromQuery_1 = searchParams?.get(COOKIES.recaptcha);
      const codeFromCookie_1 = getBrowserCookie(COOKIES.recaptcha);
      const codeResolved_1 = codeFromQuery_1 ?? codeFromCookie_1;
      if (!codeResolved_1) {
        return;
      }
      if (codeResolved_1 !== codeFromCookie_1) {
        setBrowserCookie(COOKIES.recaptcha, codeResolved_1);
      }
      setIsRecaptchaEnabled(codeResolved_1 !== "false");
    };
    $[11] = searchParams;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const loadRecaptchaEnabled = t6;
  let t7;
  let t8;
  if ($[13] !== isPageVisible || $[14] !== loadRecaptchaEnabled || $[15] !== loadRoyaltyCode || $[16] !== loadSocialladderAmbassadorId || $[17] !== loadWebSourceCode) {
    t7 = () => {
      if (isPageVisible) {
        loadRoyaltyCode();
        loadWebSourceCode();
        loadRecaptchaEnabled();
        loadSocialladderAmbassadorId();
      }
    };
    t8 = [isPageVisible, loadRecaptchaEnabled, loadRoyaltyCode, loadWebSourceCode, loadSocialladderAmbassadorId];
    $[13] = isPageVisible;
    $[14] = loadRecaptchaEnabled;
    $[15] = loadRoyaltyCode;
    $[16] = loadSocialladderAmbassadorId;
    $[17] = loadWebSourceCode;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[20] !== isRecaptchaEnabled || $[21] !== royaltyCode || $[22] !== socialladderAmbassadorId || $[23] !== webSourceCode) {
    t9 = () => {
      log.info(`${logNampsace} STATUS`, {
        royaltyCode,
        webSourceCode,
        socialladderAmbassadorId,
        isRecaptchaEnabled
      });
    };
    t10 = [royaltyCode, webSourceCode, socialladderAmbassadorId, isRecaptchaEnabled];
    $[20] = isRecaptchaEnabled;
    $[21] = royaltyCode;
    $[22] = socialladderAmbassadorId;
    $[23] = webSourceCode;
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  if ($[26] !== isRecaptchaEnabled || $[27] !== royaltyCode || $[28] !== socialladderAmbassadorId || $[29] !== webSourceCode) {
    t11 = {
      royaltyCode,
      setRoyaltyCode,
      socialladderAmbassadorId,
      setSocialladderAmbassadorId,
      webSourceCode,
      setWebSourceCode,
      isRecaptchaEnabled,
      setIsRecaptchaEnabled
    };
    $[26] = isRecaptchaEnabled;
    $[27] = royaltyCode;
    $[28] = socialladderAmbassadorId;
    $[29] = webSourceCode;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== children || $[32] !== t11) {
    t12 = <TrackingCodesContext.Provider value={t11}>{children}</TrackingCodesContext.Provider>;
    $[31] = children;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
};

/** This hook returns the tracking codes used in the app. */
export const useTrackingCodes = () => {
  return useContext(TrackingCodesContext);
};
function _temp(prev) {
  return prev + 1;
}